import React, { ReactElement } from "react";

import { I18n } from "i18n-js";
import * as locales from "../../locales.json";

import NavGroup from "./navGroup";
import NavLink from "./navLink";
import Permissioned from "./permissioned";
import { Subscription } from "../../interfaces/interfaces";
import { onAdminPage, onSuperAdminPage } from "../../utils";
import IconLink from "./iconLink";
import NavContext from "./context";

const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";

const officeBuilding = require("../svg/officeBuilding.svg");
const userGroup = require("../svg/userGroup.svg");
const chat = require("../svg/chat.svg");
const document = require("../svg/documentReport.svg");
const lightbulb = require("../svg/lightbulb.svg");
const doubleChat = require("../svg/doubleChat.svg");
const plant = require("../svg/plant.svg");
const viewGrid = require("../svg/viewGrid.svg");
const arrowRight = require("../svg/arrowRight.svg");
const calendarSolid = require("../svg/calendarSolid.svg");

interface AdminNavSectionProps {
  chargebeeSite: string;
  expandedGroup: string;
  handleGroupClick: any;
  handleInitialLoad: any;
  isDemo: boolean;
  isMobile: boolean;
  isTrialing: boolean;
  location: string;
  navigationShrunk: boolean;
  pendingAwardsClaims: number;
  permissions: string[];
  productTier: string;
  subscription: Subscription;
  unresolvedCount: number;
  userManagmentIntegrationURL?: string;
}

export default class AdminNavSection extends React.Component<
  AdminNavSectionProps,
  any
> {
  static contextType = NavContext;

  static defaultProps = {
    expandedGroup: null,
  };

  render(): ReactElement {
    const showAdminMenu =
      !this.props.isMobile &&
      (onAdminPage(this.props.location) ||
        onSuperAdminPage(this.props.location));

    if (showAdminMenu) {
      return (
        <Permissioned
          allowedPermissions={[
            "admin",
            "awards",
            "billing",
            "importedUsers",
            "rewards",
            "tech",
          ]}
          userPermissions={this.props.permissions}
        >
          {this.renderUpdatedSidebarTrialCTASection()}
          <div className="link-grouping admin-links">
            {this.renderDashboard()}
            {this.renderCompany()}
            {this.renderUsers()}
            {this.renderBonuses()}
            {this.renderGrowth()}
            {this.renderReports()}
            {this.renderExternalLinks()}
          </div>
        </Permissioned>
      );
    }

    return null;
  }

  renderDashboard = (): ReactElement => {
    return (
      <Permissioned
        allowedPermissions={["admin", "billing"]}
        userPermissions={this.props.permissions}
      >
        <IconLink
          href={this.monarchURL("/admin/dashboard")}
          label="Dashboard"
          iconAlt="Menu icon"
          iconSrc={viewGrid}
          style={{ marginBottom: "0.5rem" }}
        />
      </Permissioned>
    );
  };

  renderCompany = (): ReactElement => {
    return (
      <Permissioned
        allowedPermissions={[
          "admin",
          "billing",
          "importedUsers",
          "rewards",
          "tech",
        ]}
        userPermissions={this.props.permissions}
      >
        <NavGroup
          navigationShrunk={this.props.navigationShrunk}
          location={this.props.location}
          onInitialLoad={this.props.handleInitialLoad}
          groupId="company"
          expandedId={this.props.expandedGroup}
          onClick={this.props.handleGroupClick}
          label={i18n.translate("navigation.company")}
          iconSvg={<img src={officeBuilding} />}
        >
          {this.renderAccountSettings()}
          {this.renderIntegrations()}
          {this.renderBillingSettings()}
          {this.renderBillingActivity()}
          {this.renderRewardsCatalog()}
          {this.renderProgramsSettings()}
          {this.renderAnnouncements()}
        </NavGroup>
      </Permissioned>
    );
  };

  renderAccountSettings = (): ReactElement => {
    return (
      <Permissioned
        url={this.monarchURL("/admin/company/settings")}
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.account_settings")}
          url={this.monarchURL("/admin/company/settings")}
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderBillingSettings = (): ReactElement => {
    return (
      <Permissioned
        url="/company/billing"
        allowedPermissions={["billing"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.plans_and_billing")}
          url="/company/billing"
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderBillingActivity = (): ReactElement => {
    return (
      <Permissioned
        url="/company/billing_activity"
        allowedPermissions={["billing"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.billing_history")}
          url="/company/billing_activity"
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderProgramsSettings = (): ReactElement => {
    return (
      <Permissioned
        url={this.monarchURL("/admin/programs/settings")}
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.programs_management")}
          url={this.monarchURL("/admin/programs/settings")}
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderAnnouncements = (): ReactElement => {
    return (
      <Permissioned
        url={this.monarchURL("/admin/company/announcements")}
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.announcements")}
          url={this.monarchURL("/admin/company/announcements")}
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderUsers = (): ReactElement => {
    return (
      <Permissioned
        allowedPermissions={["admin", "importedUsers"]}
        userPermissions={this.props.permissions}
      >
        <NavGroup
          navigationShrunk={this.props.navigationShrunk}
          location={this.props.location}
          onInitialLoad={this.props.handleInitialLoad}
          groupId="users"
          expandedId={this.props.expandedGroup}
          onClick={this.props.handleGroupClick}
          label={i18n.translate("navigation.users")}
          iconSvg={<img src={userGroup} />}
        >
          {this.renderUsersManage()}
          {this.renderUserManagementIntegration()}
          {this.renderUserImport()}
        </NavGroup>
      </Permissioned>
    );
  };

  renderUsersManage = (): ReactElement => {
    return (
      <Permissioned
        url="/company/users"
        allowedPermissions={["admin"]}
        selectedUrlPrefix="/company/users"
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.user_accounts")}
          url="/company/users"
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderEarnings = (): ReactElement => {
    return (
      <Permissioned
        url="/company/accounts"
        allowedPermissions={["billing"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.total_earned")}
          url="/company/accounts"
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderUsersGivings = (): ReactElement => {
    if (this.props.productTier === "tier_one") {
      return null;
    }

    return (
      <Permissioned
        url="/company/reports/giving"
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.total_given")}
          url="/company/reports/giving"
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderUsersParticipation = (): ReactElement => {
    if (this.props.productTier === "tier_one") {
      return null;
    }

    return (
      <Permissioned
        url="/company/users/participation"
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.participation_summary")}
          url="/company/users/participation"
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderUserManagementIntegration = (): ReactElement => {
    const url = !this.props.userManagmentIntegrationURL
      ? "/company/integrations/user_managements"
      : this.props.userManagmentIntegrationURL;

    return (
      <Permissioned
        url={url}
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.user_management_integration")}
          url={url}
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderUserImport = (): ReactElement => {
    if (this.props.userManagmentIntegrationURL) {
      return null;
    }

    const url = "/company/imported_users";
    return (
      <Permissioned
        url={url}
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.user_import")}
          url={url}
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderBonuses = (): ReactElement => {
    return (
      <Permissioned
        allowedPermissions={["admin", "awards", "awardApprover"]}
        userPermissions={this.props.permissions}
      >
        <NavGroup
          navigationShrunk={this.props.navigationShrunk}
          location={this.props.location}
          onInitialLoad={this.props.handleInitialLoad}
          groupId="bonuses"
          expandedId={this.props.expandedGroup}
          onClick={this.props.handleGroupClick}
          label={i18n.translate("navigation.recognition")}
          iconSvg={<img src={chat} />}
          badgeCounts={[
            this.props.pendingAwardsClaims,
            this.props.unresolvedCount,
          ]}
        >
          {this.renderPeerToPeer()}
          {this.renderBonusesAwards()}
          {this.renderPendingAwards()}
          {this.renderReported()}
        </NavGroup>
      </Permissioned>
    );
  };

  renderPeerToPeer = (): ReactElement => {
    if (this.props.productTier === "tier_one") {
      return null;
    }

    return (
      <Permissioned
        url={this.monarchURL("/admin/programs/peer-to-peer")}
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.peer_to_peer")}
          url={this.monarchURL("/admin/programs/peer-to-peer")}
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderBonusesManage = (): ReactElement => {
    return (
      <Permissioned
        url="/company/reports/bonuses"
        allowedPermissions={["admin", "awards"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.recognition_activity")}
          url="/company/reports/bonuses"
        />
      </Permissioned>
    );
  };

  renderBonusesAwards = (): ReactElement => {
    return (
      <Permissioned
        url="/admin/awards"
        allowedPermissions={["admin", "awards"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.awards_management")}
          url="/admin/awards"
        />
      </Permissioned>
    );
  };

  renderPendingAwards = (): ReactElement => {
    return (
      <Permissioned
        url="/admin/awards/pending"
        allowedPermissions={["admin", "awards", "awardApprover"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.pending_awards")}
          url="/admin/awards/pending"
          badgeCount={this.props.pendingAwardsClaims}
        />
      </Permissioned>
    );
  };

  renderReported = (): ReactElement => {
    return (
      <Permissioned
        url="/company/bonus_reports"
        allowedPermissions={["admin"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.reported_posts")}
          url="/company/bonus_reports"
          badgeCount={this.props.unresolvedCount}
        />
      </Permissioned>
    );
  };

  renderRewardsManage = (): ReactElement => {
    return (
      <Permissioned
        url="/company/rewards?aasm_state=approved&range=all"
        allowedPermissions={["rewards"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.redemptions_activity")}
          url="/company/rewards?aasm_state=approved&range=all"
        />
      </Permissioned>
    );
  };

  renderRewardsCatalog = (): ReactElement => {
    return (
      <Permissioned
        allowedPermissions={["rewards"]}
        url={this.monarchURL("/admin/rewards/catalog")}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.rewards_catalog")}
          url={this.monarchURL("/admin/rewards/catalog")}
        />
      </Permissioned>
    );
  };

  renderRewardsTotals = (): ReactElement => {
    return (
      <Permissioned
        url="/company/rewards/totals_report"
        allowedPermissions={["billing"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.rewards_cost")}
          url="/company/rewards/totals_report"
        />
      </Permissioned>
    );
  };

  renderIntegrations = () => {
    return (
      <Permissioned
        url="/company/settings/integrations"
        allowedPermissions={["admin", "tech"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.integrations")}
          url="/company/settings/integrations"
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderGrowth = (): ReactElement => {
    return (
      <Permissioned
        allowedPermissions={["growth"]}
        userPermissions={this.props.permissions}
      >
        <NavGroup
          navigationShrunk={this.props.navigationShrunk}
          location={this.props.location}
          onInitialLoad={this.props.handleInitialLoad}
          groupId="growth"
          expandedId={this.props.expandedGroup}
          onClick={this.props.handleGroupClick}
          label={i18n.translate("navigation.growth")}
          iconSvg={<img src={plant} />}
        >
          {this.renderOneOnOnesOverview()}
        </NavGroup>
      </Permissioned>
    );
  };

  renderOneOnOnesOverview = () => {
    return (
      <Permissioned
        url={this.monarchURL("/admin/dashboard/one-on-ones")}
        allowedPermissions={["growth"]}
        userPermissions={this.props.permissions}
      >
        <NavLink
          location={this.props.location}
          label={i18n.translate("navigation.one_on_ones_overview")}
          url={this.monarchURL("/admin/dashboard/one-on-ones")}
          icon="fa"
        />
      </Permissioned>
    );
  };

  renderReports = () => {
    return (
      <Permissioned
        allowedPermissions={["admin", "awards", "billing", "rewards"]}
        userPermissions={this.props.permissions}
      >
        <NavGroup
          navigationShrunk={this.props.navigationShrunk}
          location={this.props.location}
          onInitialLoad={this.props.handleInitialLoad}
          groupId="reports"
          expandedId={this.props.expandedGroup}
          onClick={this.props.handleGroupClick}
          label={i18n.translate("navigation.reports")}
          iconSvg={<img src={document} />}
        >
          {this.renderBonusesManage()}
          {this.renderUsersParticipation()}
          {this.renderEarnings()}
          {this.renderUsersGivings()}
          {this.renderRewardsManage()}
          {this.renderRewardsTotals()}
        </NavGroup>
      </Permissioned>
    );
  };

  renderExternalLinks = () => {
    return (
      <div className="external-links">
        <a
          className="external-link"
          title={i18n.translate("shared.header.whats_new")}
          href="https://help.bonus.ly/en/articles/4076250-product-updates-and-new-features"
          target="_blank"
        >
          <div className="icon">
            <img src={lightbulb} />
          </div>
          <div className="text">
            {i18n.translate("shared.header.whats_new")}
          </div>
        </a>
        <a
          className="external-link"
          title={i18n.translate("shared.header.help_and_support")}
          href="http://help.bonus.ly"
          target="_blank"
        >
          <div className="icon">
            <img src={doubleChat} />
          </div>
          <div className="text">
            {i18n.translate("shared.header.help_and_support")}
          </div>
        </a>
      </div>
    );
  };

  renderUpdatedSidebarTrialCTASection = () => {
    if (
      this.props.subscription.showUpdatedSidebarTrialCTA &&
      !this.props.isMobile &&
      !this.props.isDemo
    ) {
      return (
        <div className="updated-sidebar-cta">
          {this.props.subscription.inTrial ? (
            <span className="updated-trial-copy">
              <img src={calendarSolid} />{" "}
              <span className="days-left">
                {this.props.subscription.daysLeft}{" "}
                {i18n.translate("shared.header.days")}
              </span>{" "}
              {i18n.translate("shared.header.left_in_free_trial")}
            </span>
          ) : (
            <>{i18n.translate("shared.header.your_trial_has_ended")}</>
          )}

          <a
            id="invite-teammates-button"
            className="btn"
            href="#"
            onClick={(e): void => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("open-user-invite-modal"));
              window.amplitude.track("Updated Sidebar CTA: Invite Teammates");
            }}
          >
            {i18n.translate("shared.header.invite_teammates")}
          </a>
          <a id="manage-plan-button" className="btn" href="/company/billing">
            {i18n.translate("shared.header.manage_plan")}
            <img src={arrowRight} />
          </a>
        </div>
      );
    }

    return null;
  };

  monarchURL(href: string): string {
    const cleanBase = (this.context as any).monarchBaseURL.replace(/\/$/, "");
    const cleanHref = href.replace(/^\//, "");

    return [cleanBase, cleanHref].join("/");
  }
}
